import { useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { customFetch, getGetMeQueryKey } from "@workspace/api-client-react";
import { extractApiErrorMessage } from "@/lib/api-error";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  Users, TrendingUp, MousePointerClick, ShoppingCart, CreditCard, Search,
  ChevronLeft, ChevronRight, Filter, Plus, X, MoreVertical, Link2, Check, Copy, Trash2, Send, Unlock, HandCoins,
  KeyRound, AlertCircle, CheckCircle2, HelpCircle, History, RefreshCw, ExternalLink, Undo2,
  Bot,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SegmentPieCard } from "@/components/admin/SegmentPieCard";
import { VerAgenteModal, lerConversaDaUrl } from "@/components/admin/VerAgenteModal";
import { ProvedoresWhatsappPanel } from "@/components/admin/ProvedoresWhatsapp";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { SectionErrorBoundary } from "@/components/ErrorBoundary";

const EVENT_COLORS: Record<string, string> = {
  Lead: "#3B82F6",
  CompleteRegistration: "#8B5CF6",
  StartTrial: "#F59E0B",
  InitiateCheckout: "#F97316",
  Subscribe: "#10B981",
};

const EVENT_LABELS: Record<string, string> = {
  Lead: "Lead",
  CompleteRegistration: "Cadastro",
  StartTrial: "1ª Venda",
  InitiateCheckout: "Checkout",
  Subscribe: "Assinatura",
};

const EVENT_ICONS: Record<string, typeof Users> = {
  Lead: MousePointerClick,
  CompleteRegistration: Users,
  StartTrial: ShoppingCart,
  InitiateCheckout: CreditCard,
  Subscribe: TrendingUp,
};

const EVENTS_ORDER = ["Lead", "CompleteRegistration", "StartTrial", "InitiateCheckout", "Subscribe"];

const PLATFORM_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "organic", label: "Orgânico" },
  { value: "meta", label: "Meta" },
  { value: "google", label: "Google" },
  { value: "tiktok", label: "TikTok" },
  { value: "microsoft", label: "Microsoft" },
  { value: "x", label: "X" },
];

function formatDate(d: Date) {
  return d.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d
    .toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(", ", " - ");
}

function formatShortDate(dateStr: string) {
  const [, m, d] = dateStr.split("-");
  return `${d}/${m}`;
}

function usePersistedBoolean(key: string, defaultValue: boolean): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return raw === "1";
    } catch {
      return defaultValue;
    }
  });
  const set = (v: boolean) => {
    setValue(v);
    try { localStorage.setItem(key, v ? "1" : "0"); } catch {}
  };
  return [value, set];
}

// Data pra tela, em BRT. Duas armadilhas, uma em cada sentido:
// 1. A Busca Geral vem de SQL cru, onde o timestamp chega "2026-04-12
//    12:33:14.537109+00" (espaço, não T). O `split("T")[0]` de antes devolvia
//    a string inteira como "dia" e a coluna virava "12 12:33:14.537109+00/04/2026".
//    O `+00` também não é ISO válido — precisa virar "+00:00" pra Safari.
// 2. Data PURA ("2026-09-25", como next_due_date) não pode passar por fuso:
//    vira meia-noite UTC, que é 21h BRT do dia ANTERIOR. Essa sai por fatia.
// O dia tem que ser o de BRT porque é ele que ancora a cota do plano grátis
// (getCycleStart vira no dia do mês da criação, 00:00 BRT) — medido em
// 21/09/2026: 2.534 lojas, 2.489 delas no grátis, nasceram entre 21h e meia-
// noite e apareciam com um dia a mais.
// "2026-10-21T14:45" no relógio de Brasília — o formato do
// <input type="datetime-local">. O "Liberar até" é lido em BRT no servidor,
// então a tela também parte de BRT, não do fuso do navegador.
const agoraEmBrasilia = () =>
  new Date().toLocaleString("sv-SE", { timeZone: "America/Sao_Paulo" }).replace(" ", "T").slice(0, 16);

function fmtDate(s: string | null): string {
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  const t = new Date(s.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00"));
  if (Number.isNaN(t.getTime())) return "—";
  return t.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

function CollapsibleSection({
  title,
  open,
  onToggle,
  controls,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  controls?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-col space-y-1.5 p-6 pt-[22px] pb-[22px]">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
            <CardTitle className="text-base">{title}</CardTitle>
          </button>
          {open && controls && (
            <div className="flex items-center gap-2">{controls}</div>
          )}
        </div>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
}

export default function Results() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  // «Ver Agente» pelo link direto /results?conversa=<storeId>:<phone> (lido uma vez e tirado da URL).
  const [conversaDoLink, setConversaDoLink] = useState(() => lerConversaDaUrl());
  const now = new Date();
  const defaultFrom = new Date(2026, 3, 9);

  const [dateFrom, setDateFrom] = useState(formatDate(defaultFrom));
  const [dateTo, setDateTo] = useState(formatDate(now));
  const [platform, setPlatform] = useState("all");
  const [utmCampaign, setUtmCampaign] = useState("all");
  const [convPage, setConvPage] = useState(1);
  const [convSearch, setConvSearch] = useState("");
  const [convEvents, setConvEvents] = useState<string[]>([]);
  const [convOpen, setConvOpen] = usePersistedBoolean("results.convOpen", false);
  const [errOpen, setErrOpen] = usePersistedBoolean("results.errOpen", false);
  // Default true porque a tabela Usuários (que abria por padrão) foi removida —
  // agora Assinaturas Asaas é a tabela principal e deve abrir aberta.
  const [subsOpen, setSubsOpen] = usePersistedBoolean("results.subsOpen", true);
  const [errPage, setErrPage] = useState(1);
  const [errSearch, setErrSearch] = useState("");
  const [rateLines, setRateLines] = useState<Array<{ from: string; to: string; color?: string }>>([
    { from: "Lead", to: "CompleteRegistration" },
  ]);
  const [newRateColor, setNewRateColor] = useState("#3B82F6");
  const [newRateFrom, setNewRateFrom] = useState("CompleteRegistration");
  const [newRateTo, setNewRateTo] = useState("Subscribe");

  const handleImpersonate = useCallback(async (storeId: string) => {
    try {
      await customFetch("/api/admin/results/impersonate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId }),
      });
      // A chave é a do cliente gerado (`/api/auth/me`), que é de onde o painel
      // inteiro tira usuário e loja. Invalidar "auth-user" — que não é chave de
      // ninguém — era um no-op: o painel abria com a loja ANTERIOR e só trocava
      // no F5. O resto do cache também é da loja anterior, então cai junto.
      // O await no /auth/me é o que evita a piscada: sem ele a navegação
      // acontece antes de a identidade nova chegar.
      queryClient.invalidateQueries();
      await queryClient.refetchQueries({ queryKey: getGetMeQueryKey() });
      setLocation("/");
    } catch (err) {
      console.error("Impersonate error:", err);
    }
  }, [queryClient, setLocation]);

  const queryParams = `from=${dateFrom}&to=${dateTo}&platform=${platform}&utm_campaign=${utmCampaign}`;

  const { data: stats } = useQuery({
    queryKey: ["admin-results-stats", queryParams],
    queryFn: () => customFetch<Record<string, number>>(`/api/admin/results/stats?${queryParams}`),
  });

  const { data: timeline } = useQuery({
    queryKey: ["admin-results-timeline", queryParams],
    queryFn: () => customFetch<Array<{ day: string; event: string; total: number }>>(`/api/admin/results/timeline?${queryParams}`),
  });

  const { data: onboarding } = useQuery({
    queryKey: ["admin-results-onboarding", queryParams],
    queryFn: () => customFetch<Array<{ registerStep: number | null; total: number }>>(`/api/admin/results/onboarding?${queryParams}`),
  });

  const { data: filters } = useQuery({
    queryKey: ["admin-results-filters"],
    queryFn: () => customFetch<{ campaigns: string[] }>(`/api/admin/results/filters`),
  });

  const { data: subsData } = useQuery<{
    metrics: { mrr: number; active: number; professional: number; pix: number; card: number; inactive: number; total: number };
    subscriptions: any[];
  }>({
    queryKey: ["admin-results-subscriptions"],
    queryFn: () => customFetch("/api/admin/results/subscriptions"),
  });

  const fmtBRLTop = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const convEventParam = convEvents.length > 0 ? convEvents.join(",") : "all";

  const { data: conversions } = useQuery({
    queryKey: ["admin-results-conversions", queryParams, convPage, convSearch, convEventParam],
    queryFn: () => customFetch<{ data: any[]; total: number; page: number; totalPages: number }>(
      `/api/admin/results/conversions?${queryParams}&page=${convPage}&search=${encodeURIComponent(convSearch)}&event=${convEventParam}`
    ),
  });

  useEffect(() => { setConvPage(1); }, [convSearch, convEventParam, queryParams]);
  useEffect(() => { setErrPage(1); }, [errSearch, queryParams]);

  interface ClientErrorRow {
    id: string;
    createdAt: string;
    message: string | null;
    errorName: string | null;
    stack: string | null;
    componentStack: string | null;
    url: string | null;
    pathname: string | null;
    appVersion: string | null;
    userAgent: string | null;
    storeId: string | null;
    storeName: string | null;
    userId: string | null;
    userEmail: string | null;
  }

  const { data: clientErrors } = useQuery({
    queryKey: ["admin-results-client-errors", queryParams, errPage, errSearch],
    queryFn: () => customFetch<{ data: ClientErrorRow[]; total: number; page: number; totalPages: number }>(
      `/api/admin/results/client-errors?${queryParams}&page=${errPage}&search=${encodeURIComponent(errSearch)}`
    ),
    enabled: errOpen,
  });

  const shortBrowser = (ua: string | null | undefined): string => {
    if (!ua) return "—";
    if (/Edg\//i.test(ua)) return "Edge";
    if (/OPR\/|Opera/i.test(ua)) return "Opera";
    if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return /Mobile/i.test(ua) ? "Chrome Mobile" : "Chrome";
    if (/Firefox\//i.test(ua)) return "Firefox";
    if (/Safari\//i.test(ua)) return /Mobile/i.test(ua) ? "Safari Mobile" : "Safari";
    return "Outro";
  };

  const truncateText = (s: string | null | undefined, n: number): string => {
    if (!s) return "—";
    return s.length > n ? s.slice(0, n) + "…" : s;
  };

  const timelineData = useMemo(() => {
    if (!timeline) return [];
    const dayMap: Record<string, Record<string, number>> = {};
    for (const row of timeline) {
      if (!dayMap[row.day]) dayMap[row.day] = {};
      dayMap[row.day][row.event] = Number(row.total);
    }
    return Object.entries(dayMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, events]) => ({
        day: formatShortDate(day),
        ...EVENTS_ORDER.reduce((acc, e) => ({ ...acc, [e]: events[e] || 0 }), {}),
      }));
  }, [timeline]);

  const funnelData = useMemo(() => {
    if (!stats) return [];
    return EVENTS_ORDER.map((event, i) => ({
      name: EVENT_LABELS[event],
      value: stats[event] || 0,
      fill: EVENT_COLORS[event],
    }));
  }, [stats]);

  const rateLineMeta = useCallback((line: { from: string; to: string }) => {
    const isCount = line.from === "__none__" || line.to === "__none__";
    const event = line.to !== "__none__" ? line.to : line.from;
    if (isCount) {
      return { isCount: true as const, key: `count:${event}`, name: EVENT_LABELS[event] || event, event };
    }
    return { isCount: false as const, key: `${line.to}/${line.from}`, name: `${EVENT_LABELS[line.to] || line.to} / ${EVENT_LABELS[line.from] || line.from}`, event: line.to };
  }, []);

  const rateTimelineData = useMemo(() => {
    if (!timeline) return [];
    const dayMap: Record<string, Record<string, number>> = {};
    for (const row of timeline) {
      if (!dayMap[row.day]) dayMap[row.day] = {};
      dayMap[row.day][row.event] = Number(row.total);
    }
    return Object.entries(dayMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, events]) => {
        const point: Record<string, any> = { day: formatShortDate(day) };
        for (const line of rateLines) {
          const meta = rateLineMeta(line);
          if (meta.isCount) {
            point[meta.key] = events[meta.event] || 0;
          } else {
            const f = events[line.from] || 0;
            const t = events[line.to] || 0;
            point[meta.key] = f > 0 ? Number(((t / f) * 100).toFixed(2)) : 0;
          }
        }
        return point;
      });
  }, [timeline, rateLines, rateLineMeta]);

  const rateLineColor = useCallback((line: { from: string; to: string; color?: string }, idx: number) => {
    if (line.color) return line.color;
    const palette = ["#3B82F6", "#8B5CF6", "#F59E0B", "#10B981", "#EF4444", "#EC4899", "#06B6D4", "#84CC16"];
    const target = line.to !== "__none__" ? line.to : line.from;
    return EVENT_COLORS[target] || palette[idx % palette.length];
  }, []);

  const hasCountLine = rateLines.some((l) => l.from === "__none__" || l.to === "__none__");
  const hasRateLine = rateLines.some((l) => l.from !== "__none__" && l.to !== "__none__");

  const onboardingData = useMemo(() => {
    if (!onboarding) return [];
    const stepLabels: Record<string, string> = {
      "1": "Etapa 1 - Início",
      "2": "Etapa 2 - Dados da Loja",
      "3": "Etapa 3 - WhatsApp",
      "4": "Etapa 4 - Tipo de Loja",
      "5": "Etapa 5 - Cadastro",
      "6": "Etapa 6 - Produtos",
      "7": "Etapa 7 - Configurações",
      "8": "Etapa 8 - Finalização",
      null: "Concluído",
    };
    const total = onboarding.reduce((sum, r) => sum + Number(r.total), 0);
    return onboarding.map((r) => ({
      step: stepLabels[String(r.registerStep)] || `Etapa ${r.registerStep}`,
      total: Number(r.total),
      percent: total > 0 ? ((Number(r.total) / total) * 100).toFixed(1) : "0",
    }));
  }, [onboarding]);

  return (
    <div className="space-y-6 pb-[30px] pl-[30px] pr-[30px] pt-[30px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display">Resultados</h1>
          <p className="text-muted-foreground text-sm mt-1">Métricas de aquisição e conversão</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setLocation("/closer")}>
          Closer
        </Button>
      </div>
      <SectionErrorBoundary label="Busca Geral">
        <GlobalSearch onImpersonate={handleImpersonate} />
      </SectionErrorBoundary>
      <ProvedoresWhatsappPanel />
      <SlugMessagesPanel />
      <div className="flex flex-wrap items-end gap-3 p-4 bg-muted/50 rounded-xl border">
        <div className="flex items-center gap-1 text-sm text-muted-foreground font-medium"><Filter className="w-4 h-4" /> Filtros</div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground font-medium">De</label>
          <DataBr value={dateFrom} onChange={setDateFrom} className="h-9 px-3 rounded-md border text-sm bg-background" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground font-medium">Até</label>
          <DataBr value={dateTo} onChange={setDateTo} className="h-9 px-3 rounded-md border text-sm bg-background" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground font-medium">Plataforma</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-9 px-3 rounded-md border text-sm bg-background min-w-[120px]">
            {PLATFORM_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground font-medium">UTM Campaign</label>
          <select value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} className="h-9 px-3 rounded-md border text-sm bg-background min-w-[180px]">
            <option value="all">Todas</option>
            {filters?.campaigns?.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <SectionErrorBoundary label="Métricas Topo">
        <div className="grid grid-cols-5 gap-4">
          {EVENTS_ORDER.map((event) => {
            const Icon = EVENT_ICONS[event];
            return (
              <Card key={event} className="border-l-4" style={{ borderLeftColor: EVENT_COLORS[event] }}>
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{EVENT_LABELS[event]}</p>
                    <Icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <p className="text-3xl font-bold mt-1">{stats?.[event] ?? "—"}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </SectionErrorBoundary>
      <SectionErrorBoundary label="Assinaturas Asaas">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Assinaturas Asaas</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Card><CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wide">MRR</p>
              <p className="text-lg font-bold">{subsData ? fmtBRLTop(subsData.metrics.mrr) : "—"}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Ativas</p>
              <p className="text-lg font-bold">{subsData?.metrics.active ?? "—"}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Profissionais</p>
              <p className="text-lg font-bold">{subsData?.metrics.professional ?? "—"}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wide">PIX Ativas</p>
              <p className="text-lg font-bold">{subsData?.metrics.pix ?? "—"}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Cartão Ativas</p>
              <p className="text-lg font-bold">{subsData?.metrics.card ?? "—"}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Inativas</p>
              <p className="text-lg font-bold">{subsData?.metrics.inactive ?? "—"}</p>
            </CardContent></Card>
          </div>
        </div>
      </SectionErrorBoundary>
      <div className="grid grid-cols-2 gap-4">
        <SectionErrorBoundary label="Funil de Conversão">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Funil de Conversão</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {funnelData.map((item, i) => {
                const maxVal = Math.max(...funnelData.map((d) => d.value), 1);
                const width = Math.max((item.value / maxVal) * 100, 8);
                const prevVal = i > 0 ? funnelData[i - 1].value : null;
                const rate = prevVal && prevVal > 0 ? ((item.value / prevVal) * 100).toFixed(1) : null;
                return (
                  <div key={item.name}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-muted-foreground">
                        {item.value}
                        {rate && <span className="ml-2 text-xs">({rate}%)</span>}
                      </span>
                    </div>
                    <div className="h-8 rounded-md overflow-hidden bg-muted">
                      <div
                        className="h-full rounded-md transition-all duration-500"
                        style={{ width: `${width}%`, backgroundColor: item.fill }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        </SectionErrorBoundary>

        <SectionErrorBoundary label="Nicho dos assinantes">
          <SegmentPieCard />
        </SectionErrorBoundary>

        <SectionErrorBoundary label="Onboarding">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Onboarding</CardTitle>
          </CardHeader>
          <CardContent>
            {onboardingData.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem dados</p>
            ) : (
              <div className="space-y-2">
                {onboardingData.map((item) => {
                  const maxVal = Math.max(...onboardingData.map((d) => d.total), 1);
                  const width = Math.max((item.total / maxVal) * 100, 8);
                  return (
                    <div key={item.step}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-xs">{item.step}</span>
                        <span className="text-muted-foreground text-xs">{item.total} ({item.percent}%)</span>
                      </div>
                      <div className="h-6 rounded-md overflow-hidden bg-muted">
                        <div className="h-full rounded-md bg-primary/70 transition-all duration-500" style={{ width: `${width}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        </SectionErrorBoundary>
      </div>
      <SectionErrorBoundary label="Taxas de Conversão por Dia">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base">Taxas de Conversão por Dia</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={newRateTo} onValueChange={setNewRateTo}>
                  <SelectTrigger className="h-8 w-[140px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    {EVENTS_ORDER.map((e) => <SelectItem key={e} value={e}>{EVENT_LABELS[e]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">/</span>
                <Select value={newRateFrom} onValueChange={setNewRateFrom}>
                  <SelectTrigger className="h-8 w-[140px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    {EVENTS_ORDER.map((e) => <SelectItem key={e} value={e}>{EVENT_LABELS[e]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label
                  className="relative h-8 w-8 rounded-md border cursor-pointer flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: newRateColor }}
                  title="Cor da linha"
                >
                  <input
                    type="color"
                    value={newRateColor}
                    onChange={(e) => setNewRateColor(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  disabled={
                    newRateFrom === newRateTo ||
                    (newRateFrom === "__none__" && newRateTo === "__none__") ||
                    rateLines.some((r) => r.from === newRateFrom && r.to === newRateTo) ||
                    // count line already exists for this event
                    ((newRateFrom === "__none__" || newRateTo === "__none__") &&
                      rateLines.some((r) => {
                        const ev = newRateTo !== "__none__" ? newRateTo : newRateFrom;
                        return (r.from === "__none__" && r.to === ev) || (r.to === "__none__" && r.from === ev);
                      }))
                  }
                  onClick={() => setRateLines((prev) => [...prev, { from: newRateFrom, to: newRateTo, color: newRateColor }])}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Adicionar
                </Button>
              </div>
            </div>
            {rateLines.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {rateLines.map((line, idx) => {
                  const meta = rateLineMeta(line);
                  return (
                    <span
                      key={meta.key}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border"
                      style={{ borderColor: rateLineColor(line, idx), color: rateLineColor(line, idx) }}
                    >
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: rateLineColor(line, idx) }} />
                      {meta.name}
                      <button
                        type="button"
                        onClick={() => setRateLines((prev) => prev.filter((_, i) => i !== idx))}
                        className="ml-1 hover:text-foreground"
                        aria-label="Remover"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {rateLines.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Adicione uma taxa de conversão para começar</p>
            ) : rateTimelineData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Sem dados no período</p>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={rateTimelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" fontSize={12} />
                  <YAxis
                    fontSize={12}
                    tickFormatter={(v) => hasRateLine && !hasCountLine ? `${v}%` : `${v}`}
                  />
                  <Tooltip
                    formatter={(v: any, name: any) => {
                      const isRate = typeof name === "string" && name.includes(" / ");
                      return isRate ? `${v}%` : v;
                    }}
                  />
                  <Legend />
                  {rateLines.map((line, idx) => {
                    const meta = rateLineMeta(line);
                    return (
                      <Line
                        key={meta.key}
                        type="monotone"
                        dataKey={meta.key}
                        name={meta.name}
                        stroke={rateLineColor(line, idx)}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </SectionErrorBoundary>
      <SectionErrorBoundary label="Assinaturas">
        <SubscriptionsPanel
          open={subsOpen}
          onToggle={() => setSubsOpen(!subsOpen)}
          onImpersonate={handleImpersonate}
        />
      </SectionErrorBoundary>
      <SectionErrorBoundary label="Conversões">
        <CollapsibleSection
          title="Conversões"
          open={convOpen}
          onToggle={() => setConvOpen(!convOpen)}
          controls={
            <>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 gap-1 text-sm font-normal">
                      {convEvents.length === 0
                        ? "Eventos"
                        : convEvents.length === 1
                        ? EVENT_LABELS[convEvents[0]]
                        : `${convEvents.length} eventos`}
                      <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2" align="end">
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-muted">
                        <Checkbox
                          checked={convEvents.length === 0}
                          onCheckedChange={() => setConvEvents([])}
                        />
                        Todos
                      </label>
                      {EVENTS_ORDER.map((ev) => (
                        <label key={ev} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-muted">
                          <Checkbox
                            checked={convEvents.includes(ev)}
                            onCheckedChange={(checked) => {
                              setConvEvents((prev) =>
                                checked ? [...prev, ev] : prev.filter((e) => e !== ev)
                              );
                            }}
                          />
                          {EVENT_LABELS[ev]}
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="relative w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar nome, whatsapp, email..."
                    value={convSearch}
                    onChange={(e) => setConvSearch(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                </div>
            </>
          }
        >
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="pb-2 pr-3 w-[140px]">Data/Hora</th>
                    <th className="pb-2 pr-3 w-[100px]">Evento</th>
                    <th className="pb-2 pr-3 w-[90px]">Plataforma</th>
                    <th className="pb-2 pr-3 w-[130px]">Nome</th>
                    <th className="pb-2 pr-3 w-[140px]">WhatsApp</th>
                    <th className="pb-2 pr-3 w-[100px]">Estado</th>
                    <th className="pb-2 pr-3 w-[160px]">Campaign</th>
                    <th className="pb-2 pr-3 w-[130px]">Ad</th>
                  </tr>
                </thead>
                <tbody>
                  {conversions?.data?.map((c: any) => (
                    <tr key={c.id} className="border-b border-muted/50 hover:bg-muted/30">
                      <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(c.createdAt)}</td>
                      <td className="py-2 pr-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: EVENT_COLORS[c.event] || "#6B7280" }}>
                          {EVENT_LABELS[c.event] || c.event}
                        </span>
                      </td>
                      <td className="py-2 pr-3">{c.platform || "Orgânico"}</td>
                      <td className="py-2 pr-3 truncate">{c.firstName || "—"}</td>
                      <td className="py-2 pr-3 truncate">{c.whatsappFormatted || c.phone || "—"}</td>
                      <td className="py-2 pr-3 truncate">{c.state || "—"}</td>
                      <td className="py-2 pr-3 truncate">{c.utmCampaign || "—"}</td>
                      <td className="py-2 pr-3 truncate">{c.utmAd || "—"}</td>
                    </tr>
                  ))}
                  {(!conversions?.data || conversions.data.length === 0) && (
                    <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Nenhuma conversão encontrada</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {conversions && conversions.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 text-sm">
                <span className="text-muted-foreground">{conversions.total} registros</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={convPage <= 1} onClick={() => setConvPage(convPage - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span>Página {convPage} de {conversions.totalPages}</span>
                  <Button variant="outline" size="sm" disabled={convPage >= conversions.totalPages} onClick={() => setConvPage(convPage + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
        </CollapsibleSection>
      </SectionErrorBoundary>
      <SectionErrorBoundary label="Erros de Clientes">
        <CollapsibleSection
          title="Erros de Clientes"
          open={errOpen}
          onToggle={() => setErrOpen(!errOpen)}
          controls={
            <div className="relative w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar mensagem, loja, e-mail..."
                value={errSearch}
                onChange={(e) => setErrSearch(e.target.value)}
                className="pl-9 h-8 text-sm"
              />
            </div>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="pb-2 pr-3 w-[140px]">Data/Hora</th>
                  <th className="pb-2 pr-3 w-[140px]">Loja</th>
                  <th className="pb-2 pr-3 w-[180px]">Usuário</th>
                  <th className="pb-2 pr-3 w-[180px]">URL</th>
                  <th className="pb-2 pr-3">Mensagem</th>
                  <th className="pb-2 pr-3 w-[90px]">Versão</th>
                  <th className="pb-2 pr-3 w-[110px]">Navegador</th>
                </tr>
              </thead>
              <tbody>
                {clientErrors?.data?.map((e) => {
                  const fullDetail = [
                    e.errorName ? `${e.errorName}: ${e.message || ""}` : e.message || "",
                    e.stack ? `\n\nStack:\n${e.stack}` : "",
                    e.componentStack ? `\n\nComponent stack:${e.componentStack}` : "",
                  ].join("");
                  return (
                    <tr key={e.id} className="border-b border-muted/50 hover:bg-muted/30 align-top">
                      <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(e.createdAt)}</td>
                      <td className="py-2 pr-3 truncate" title={e.storeName || e.storeId || ""}>{e.storeName || (e.storeId ? "—" : "(s/ loja)")}</td>
                      <td className="py-2 pr-3 truncate" title={e.userEmail || e.userId || ""}>{e.userEmail || (e.userId ? "—" : "(s/ login)")}</td>
                      <td className="py-2 pr-3 truncate" title={e.url || ""}>{e.pathname || truncateText(e.url, 30)}</td>
                      <td className="py-2 pr-3">
                        <span className="block whitespace-pre-wrap break-words" title={fullDetail}>
                          {e.errorName && e.errorName !== "Error" ? <span className="font-medium">{e.errorName}: </span> : null}
                          {truncateText(e.message, 80)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 truncate font-mono text-xs" title={e.appVersion || ""}>{e.appVersion || "—"}</td>
                      <td className="py-2 pr-3 truncate" title={e.userAgent || ""}>{shortBrowser(e.userAgent)}</td>
                    </tr>
                  );
                })}
                {(!clientErrors?.data || clientErrors.data.length === 0) && (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Nenhum erro registrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {clientErrors && clientErrors.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <span className="text-muted-foreground">{clientErrors.total} registros</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={errPage <= 1} onClick={() => setErrPage(errPage - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span>Página {errPage} de {clientErrors.totalPages}</span>
                <Button variant="outline" size="sm" disabled={errPage >= clientErrors.totalPages} onClick={() => setErrPage(errPage + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CollapsibleSection>
      </SectionErrorBoundary>
      <StateFunnelTable />
      {conversaDoLink && (
        <VerAgenteModal storeId={conversaDoLink.storeId} conversaInicial={conversaDoLink.phone} onClose={() => setConversaDoLink(null)} />
      )}
    </div>
  );
}

function GlobalSearch({ onImpersonate }: { onImpersonate: (storeId: string) => void }) {
  interface SearchRow {
    userId: string;
    userFirstName: string | null;
    userLastName: string | null;
    userEmail: string | null;
    whatsappFormatted: string | null;
    whatsappNumber: string | null;
    registerStep: number | null;
    storeId: string | null;
    storeName: string | null;
    storeSlug: string | null;
    storeType: string | null;
    storeCreatedAt: string | null;
    productCount: number | null;
    subscriptionId: string | null;
    subscriptionStatus: string | null;
    planName: string | null;
    billingType: string | null;
    cycle: string | null;
    subValue: string | null;
    nextDueDate: string | null;
  }

  const [input, setInput] = useState("");
  // submittedQuery: só muda quando admin aperta Enter / clica Buscar. Sem
  // debounce, sem auto-fetch — Pedro quer baixa frequência e quer evitar
  // carregar a base inteira no mount da página.
  const [submittedQuery, setSubmittedQuery] = useState("");
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery<{ data: SearchRow[]; total: number }>({
    queryKey: ["admin-results-search", submittedQuery],
    queryFn: () =>
      customFetch(`/api/admin/results/search?q=${encodeURIComponent(submittedQuery)}`),
    enabled: submittedQuery.length >= 2,
    // Resultados ficam em cache por 60s (admin pode reabrir busca anterior),
    // mas não tem refetch automático — busca admin não precisa ser tempo real.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const submit = () => {
    const q = input.trim();
    if (q.length < 2) return;
    setSubmittedQuery(q);
  };

  const typeLabel = (t: string | null) =>
    t === "produtos" ? "Produtos" : t === "servicos" ? "Serviços" : t === "ambos" ? "Ambos" : "—";
  const statusBadge = (subStatus: string | null, registerStep: number | null) => {
    if (subStatus === "ACTIVE") return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">Assinante</span>;
    if (subStatus === "INACTIVE" || subStatus === "CANCELED") return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-100 text-zinc-600">Ex-assinante</span>;
    if (subStatus === "EXPIRED") return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700">Expirada</span>;
    if (registerStep !== null) return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700">Etapa {registerStep}</span>;
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700">Cadastrado</span>;
  };

  const results = data?.data ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="w-4 h-4" /> Busca Geral
          </CardTitle>
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Input
              placeholder="Buscar nome, e-mail, whatsapp, loja, slug... (Enter)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
              className="h-9 text-sm"
            />
            <Button size="sm" onClick={submit} disabled={input.trim().length < 2}>
              Buscar
            </Button>
            {submittedQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setInput(""); setSubmittedQuery(""); }}
                title="Limpar"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {!submittedQuery ? (
          <p className="text-xs text-muted-foreground">Digite ao menos 2 caracteres e aperte Enter pra buscar em lojas, donos, e-mails, whatsapps e slugs.</p>
        ) : isFetching ? (
          <p className="text-sm text-muted-foreground">Buscando…</p>
        ) : results.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum resultado para "{submittedQuery}".</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Dono / Loja</th>
                  <th className="px-3 py-2 text-left">Slug</th>
                  <th className="px-3 py-2 text-left">Criação</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-right">Produtos</th>
                  <th className="px-3 py-2 text-left">WhatsApp</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right w-10"></th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.userId} className="border-t">
                    <td className="px-3 py-2">
                      <div className="font-medium">{[r.userFirstName, r.userLastName].filter(Boolean).join(" ") || r.userEmail || "—"}</div>
                      {r.storeName && (
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={r.storeName}>
                          {r.storeName}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2"><SlugCell slug={r.storeSlug} copyValue={r.storeId} /></td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{fmtDate(r.storeCreatedAt)}</td>
                    <td className="px-3 py-2 text-xs">{typeLabel(r.storeType)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      {r.storeId ? (r.productCount ?? 0) : "—"}
                    </td>
                    <td className="px-3 py-2"><WhatsappCell number={r.whatsappFormatted} /></td>
                    <td className="px-3 py-2">{statusBadge(r.subscriptionStatus, r.registerStep)}</td>
                    <td className="px-3 py-2 text-right">
                      <SubscriptionActionsMenu
                        subscriptionId={r.subscriptionId}
                        clientName={[r.userFirstName, r.userLastName].filter(Boolean).join(" ") || r.userEmail || "este cliente"}
                        storeId={r.storeId}
                        storeName={r.storeName || ""}
                        storeSlug={r.storeSlug}
                        whatsappFormatted={r.whatsappFormatted}
                        whatsappNumber={r.whatsappNumber}
                        firstName={r.userFirstName}
                        onImpersonate={onImpersonate}
                        onDeleted={() => {
                          // Invalida resultados da busca atual + assinaturas
                          // (caso o admin tenha deletado loja/assinatura aqui).
                          queryClient.invalidateQueries({ queryKey: ["admin-results-search"] });
                          queryClient.invalidateQueries({ queryKey: ["admin-results-subscriptions"] });
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/30">
              {results.length} resultado{results.length !== 1 ? "s" : ""}{results.length >= 50 ? " (máx 50 — refine a busca se precisar)" : ""}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WhatsappCell({ number }: { number: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!number) return <span className="text-muted-foreground">—</span>;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(number);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = number;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs whitespace-nowrap">{number}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 cursor-pointer"
        onClick={onCopy}
        title="Copiar número"
      >
        {copied ? (
          <Check className="w-3 h-3 text-green-600" />
        ) : (
          <Copy className="w-3 h-3" />
        )}
      </Button>
    </div>
  );
}

function SubscriptionsPanel({
  open,
  onToggle,
  onImpersonate,
}: {
  open: boolean;
  onToggle: () => void;
  onImpersonate: (storeId: string) => void;
}) {
  // Busca filtra a tabela in-place (server-side via ?search=). Debounce
  // 250ms — diferente da busca geral do topo que exige Enter, aqui o
  // dataset é menor (só ACTIVE/INACTIVE), e admin tipicamente já está
  // olhando a tabela, então auto-search faz sentido.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Sort por data de criação (coluna "Criação" = storeCreatedAt). Default desc
  // (contas mais novas no topo); clique no cabeçalho alterna asc/desc.
  const [createdSort, setCreatedSort] = useState<"asc" | "desc">("desc");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  interface SubRow {
    id: string;
    userId: string;
    billingType: string;
    cycle: string;
    value: string;
    status: string;
    nextDueDate: string | null;
    cancelScheduledAt: string | null;
    planName: string;
    createdAt: string;
    userEmail: string | null;
    userFirstName: string | null;
    userLastName: string | null;
    whatsappFormatted: string | null;
    whatsappNumber: string | null;
    storeId: string | null;
    storeName: string | null;
    storeSlug: string | null;
    storeType: string | null;
    storeCreatedAt: string | null;
  }
  interface SubsResponse {
    metrics: {
      mrr: number;
      active: number;
      professional: number;
      pix: number;
      card: number;
      inactive: number;
      total: number;
    };
    subscriptions: SubRow[];
  }

  const queryClient = useQueryClient();
  const { data } = useQuery<SubsResponse>({
    queryKey: ["admin-results-subscriptions", debouncedSearch],
    queryFn: () =>
      customFetch<SubsResponse>(
        `/api/admin/results/subscriptions?search=${encodeURIComponent(debouncedSearch)}`,
      ),
    enabled: open,
  });

  const fmtBRL = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const billingLabel = (t: string) => {
    if (t === "PIX") return "PIX";
    if (t === "CREDIT_CARD") return "Cartão";
    return t;
  };
  const cycleLabel = (c: string) => (c === "YEARLY" || c === "ANNUALLY" ? "Anual" : "Mensal");
  // cancel_scheduled_at é setado em TODA sub ativa como rede de segurança do
  // modelo default-safe (next_due_date + grace) e rolado a cada pagamento — não
  // é pedido de cancelamento. Só conta como cancelamento real quando o alvo cai
  // em/antes do próximo vencimento; senão "Cancela em" mostraria data pra todo
  // assinante ativo.
  const isRealCancellation = (cancelScheduledAt: string | null, nextDueDate: string | null) => {
    if (!cancelScheduledAt || !nextDueDate) return false;
    return (
      new Date(cancelScheduledAt).getTime() <=
      new Date(`${nextDueDate}T12:00:00-03:00`).getTime()
    );
  };
  const statusBadge = (s: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      ACTIVE: { label: "Ativa", cls: "bg-green-100 text-green-700 border-green-200" },
      PENDING: { label: "Pendente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
      INACTIVE: { label: "Inativa", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
      CANCELED: { label: "Cancelada", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
      EXPIRED: { label: "Expirada", cls: "bg-red-100 text-red-700 border-red-200" },
    };
    const m = map[s] || { label: s, cls: "bg-muted text-muted-foreground border" };
    return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium border ${m.cls}`}>{m.label}</span>;
  };

  const typeLabel = (t: string | null) => {
    if (t === "produtos") return "Produtos";
    if (t === "servicos") return "Serviços";
    if (t === "ambos") return "Ambos";
    return "—";
  };

  const sortedSubs = useMemo(() => {
    const list = data?.subscriptions ? [...data.subscriptions] : [];
    list.sort((a, b) => {
      const at = a.storeCreatedAt ? new Date(a.storeCreatedAt).getTime() : 0;
      const bt = b.storeCreatedAt ? new Date(b.storeCreatedAt).getTime() : 0;
      return createdSort === "asc" ? at - bt : bt - at;
    });
    return list;
  }, [data, createdSort]);

  return (
    <CollapsibleSection
      title="Assinaturas Asaas"
      open={open}
      onToggle={onToggle}
      controls={
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar nome, loja, slug, whatsapp..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-8 text-sm"
          />
        </div>
      }
    >
      {!data ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <div className="space-y-4">
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-left">Slug da Loja</th>
                  <th
                    className="px-3 py-2 text-left cursor-pointer select-none whitespace-nowrap hover:text-foreground"
                    onClick={() => setCreatedSort((d) => (d === "asc" ? "desc" : "asc"))}
                  >
                    Criação {createdSort === "asc" ? "↑" : "↓"}
                  </th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-left">WhatsApp</th>
                  <th className="px-3 py-2 text-left">Plano</th>
                  <th className="px-3 py-2 text-left">Forma</th>
                  <th className="px-3 py-2 text-left">Ciclo</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Próx. cobrança</th>
                  <th className="px-3 py-2 text-left">Cancela em</th>
                  <th className="px-3 py-2 text-right w-10"></th>
                </tr>
              </thead>
              <tbody>
                {sortedSubs.length === 0 ? (
                  <tr><td colSpan={13} className="px-3 py-6 text-center text-muted-foreground">Sem assinaturas</td></tr>
                ) : sortedSubs.map((s) => (
                  <tr key={s.id} className="border-t">
                    <td className="px-3 py-2">
                      <div className="font-medium">{[s.userFirstName, s.userLastName].filter(Boolean).join(" ") || "—"}</div>
                      {s.storeName && (
                        <div className="text-xs text-muted-foreground truncate max-w-[180px]" title={s.storeName}>
                          {s.storeName}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2"><SlugCell slug={s.storeSlug} copyValue={s.storeId} /></td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{fmtDate(s.storeCreatedAt)}</td>
                    <td className="px-3 py-2 text-xs">{typeLabel(s.storeType)}</td>
                    <td className="px-3 py-2"><WhatsappCell number={s.whatsappFormatted} /></td>
                    <td className="px-3 py-2 capitalize">{s.planName}</td>
                    <td className="px-3 py-2">{billingLabel(s.billingType)}</td>
                    <td className="px-3 py-2">{cycleLabel(s.cycle)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtBRL(Number(s.value))}</td>
                    <td className="px-3 py-2">{statusBadge(s.status)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(s.nextDueDate)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {isRealCancellation(s.cancelScheduledAt, s.nextDueDate) ? (
                        <span className="text-amber-700 text-xs">{fmtDate(s.cancelScheduledAt)}</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <SubscriptionActionsMenu
                        subscriptionId={s.id}
                        clientName={[s.userFirstName, s.userLastName].filter(Boolean).join(" ") || s.userEmail || "este cliente"}
                        storeId={s.storeId}
                        storeName={s.storeName || ""}
                        storeSlug={s.storeSlug}
                        whatsappFormatted={s.whatsappFormatted}
                        whatsappNumber={s.whatsappNumber}
                        firstName={s.userFirstName}
                        onImpersonate={onImpersonate}
                        onDeleted={() => queryClient.invalidateQueries({ queryKey: ["admin-results-subscriptions"] })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}

interface StateFunnelRow {
  uf: string;
  name: string;
  registros: number;
  primeiraVenda: number;
  assinantes: number;
  subPorRegistro: number;
  subPorTrial: number;
  trialPorRegistro: number;
  spend: number;
  custoPorSub: number | null;
  custoPorTrial: number | null;
  lowSample: boolean;
}

function StateFunnelTable() {
  const [open, setOpen] = usePersistedBoolean("results.stateFunnelOpen", false);
  const [sortKey, setSortKey] = useState<keyof StateFunnelRow>("registros");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data } = useQuery<{
    states: StateFunnelRow[];
    minSample: number;
    spendTotal: number;
    spendUnidentified: number;
  }>({
    queryKey: ["admin-results-state-funnel"],
    queryFn: () => customFetch("/api/admin/results/state-funnel"),
    enabled: open,
  });

  const rows = useMemo(() => {
    const list = data?.states ? [...data.states] : [];
    list.sort((a, b) => {
      // null (custo sem assinante) ordena como "infinito" — o estado mais caro.
      const av = a[sortKey] === null ? Infinity : a[sortKey];
      const bv = b[sortKey] === null ? Infinity : b[sortKey];
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key: keyof StateFunnelRow) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
  const brl = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

  const Th = ({ label, k, align = "right" }: { label: string; k: keyof StateFunnelRow; align?: "left" | "right" }) => (
    <th
      className={`px-3 py-2 cursor-pointer select-none whitespace-nowrap hover:text-foreground ${align === "right" ? "text-right" : "text-left"}`}
      onClick={() => toggleSort(k)}
    >
      {label}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );

  return (
    <SectionErrorBoundary label="Conversão por Estado">
      <CollapsibleSection
        title="Conversão por Estado"
        open={open}
        onToggle={() => setOpen(!open)}
      >
        <p className="text-[11px] text-muted-foreground mb-2">
          Todo o histórico, por estado do cadastro (geo do IP). Gasto Meta Ads: {data ? brl(data.spendTotal) : "—"} no
          total ({data ? brl(data.spendUnidentified) : "—"} sem estado, fora da tabela). Estados com menos de{" "}
          {data?.minSample ?? 300} registros ficam em cinza — amostra pequena pra confiar na taxa/custo (não negativar
          mídia só por isso). Clique no cabeçalho pra ordenar.
        </p>
        {!data ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th label="Estado" k="name" align="left" />
                  <Th label="Registros" k="registros" />
                  <Th label="1ª Venda" k="primeiraVenda" />
                  <Th label="Assinantes" k="assinantes" />
                  <Th label="Sub/Registro" k="subPorRegistro" />
                  <Th label="Sub/1ªVenda" k="subPorTrial" />
                  <Th label="1ªVenda/Registro" k="trialPorRegistro" />
                  <Th label="Investido" k="spend" />
                  <Th label="Custo/Sub" k="custoPorSub" />
                  <Th label="Custo/1ªVenda" k="custoPorTrial" />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.uf} className={`border-t ${s.lowSample ? "text-muted-foreground/60" : ""}`}>
                    <td className="px-3 py-2 text-left whitespace-nowrap">
                      <span className="font-semibold">{s.uf}</span>{" "}
                      <span className="text-muted-foreground">{s.name}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.registros}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.primeiraVenda}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.assinantes}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(s.subPorRegistro)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(s.subPorTrial)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(s.trialPorRegistro)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{brl(s.spend)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.custoPorSub === null ? "—" : brl(s.custoPorSub)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.custoPorTrial === null ? "—" : brl(s.custoPorTrial)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>
    </SectionErrorBoundary>
  );
}

function SlugCell({ slug, copyValue }: { slug: string | null; copyValue?: string | null }) {
  // Exibe o slug (humano-legível) mas copia o valor passado em copyValue
  // (default = slug). Usamos copyValue=storeId nas tabelas admin pra colar
  // direto em queries SQL / chamadas de API.
  const [copied, setCopied] = useState(false);
  if (!slug) return <span className="text-muted-foreground text-xs">—</span>;
  const toCopy = copyValue ?? slug;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(toCopy);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = toCopy;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs truncate max-w-[140px]" title={slug}>{slug}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 cursor-pointer shrink-0"
        onClick={onCopy}
        title={copyValue ? "Copiar store ID" : "Copiar slug"}
      >
        {copied ? (
          <Check className="w-3 h-3 text-green-600" />
        ) : (
          <Copy className="w-3 h-3" />
        )}
      </Button>
    </div>
  );
}

type RefundPreview = {
  ok: boolean;
  motivo?: string;
  store: { id: string; rootId: string; slug: string | null; name: string | null; plan: string | null };
  user: { id: string; nome: string | null } | null;
  pagamento: {
    asaasPaymentId: string;
    value: number;
    status: string;
    billingType: string;
    paymentDate: string | null;
    description: string | null;
    invoiceUrl: string | null;
  } | null;
  assinatura: {
    id: string;
    authorizationId: string | null;
    account: string;
    value: string;
    cycle: string;
    status: string;
  } | null;
  saldoConta: number | null;
};

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function SubscriptionActionsMenu({
  subscriptionId,
  clientName,
  storeId,
  storeName,
  storeSlug,
  whatsappFormatted,
  whatsappNumber,
  firstName,
  onImpersonate,
  onDeleted,
}: {
  // Opcional: o mesmo menu é usado tanto na tabela de Assinaturas Asaas
  // (subscriptionId presente, mostra "Deletar Assinatura") quanto nos
  // resultados da busca geral (subscriptionId null se a loja não tem
  // assinatura — esconde a opção de deletar assinatura).
  subscriptionId: string | null;
  clientName: string;
  storeId: string | null;
  storeName: string;
  storeSlug: string | null;
  whatsappFormatted: string | null;
  whatsappNumber: string | null;
  firstName: string | null;
  onImpersonate: (storeId: string) => void;
  onDeleted: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [subLinkLoading, setSubLinkLoading] = useState(false);
  const [subLinkCopied, setSubLinkCopied] = useState(false);
  const [crmRetrying, setCrmRetrying] = useState(false);
  const [verAgente, setVerAgente] = useState(false);
  const confirm = useConfirm();
  const [unblock24hLoading, setUnblock24hLoading] = useState(false);
  const [unblockUntilOpen, setUnblockUntilOpen] = useState(false);
  const [unblockUntilValue, setUnblockUntilValue] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [manualPayLoading, setManualPayLoading] = useState(false);
  const [deleteStoreOpen, setDeleteStoreOpen] = useState(false);
  const [deleteStoreSubmitting, setDeleteStoreSubmitting] = useState(false);
  const [deleteStoreConfirmText, setDeleteStoreConfirmText] = useState("");

  const handleDelete = async () => {
    if (deleting || !subscriptionId) return;
    setDeleting(true);
    try {
      await customFetch(`/api/admin/results/subscriptions/${subscriptionId}`, {
        method: "DELETE",
      });
      toast({ title: "Assinatura deletada" });
      setConfirmOpen(false);
      onDeleted();
    } catch (err: any) {
      toast({
        title: "Erro",
        description: extractApiErrorMessage(err, "Não foi possível deletar a assinatura."),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCopySubscribeLink = async () => {
    if (subLinkLoading || !storeId) return;
    setSubLinkLoading(true);
    try {
      const res = await customFetch<{ token: string }>(
        "/api/admin/results/subscribe-link",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId }),
        },
      );
      if (!res?.token) throw new Error("Token não retornado");
      const url = `${window.location.origin}/assinar?token=${encodeURIComponent(res.token)}`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setSubLinkCopied(true);
      toast({ title: "Link copiado", description: "Cole no WhatsApp do cliente." });
      setTimeout(() => setSubLinkCopied(false), 2000);
    } catch (err: any) {
      toast({
        title: "Erro",
        description: err?.message || "Não foi possível gerar o link.",
        variant: "destructive",
      });
    } finally {
      setSubLinkLoading(false);
    }
  };

  const handleUnblock = async (days: number) => {
    if (unblock24hLoading || !storeId) return;
    const periodLabel = days === 1 ? "24h" : `${days} dias`;
    if (!(await confirm({
      title: `Liberar a loja "${storeName}" por ${periodLabel}?`,
      description: "Ela poderá receber pedidos mesmo se o limite do plano grátis tiver estourado.",
      confirmLabel: `Liberar ${periodLabel}`,
      destructive: false,
    }))) return;
    setUnblock24hLoading(true);
    try {
      const res = await customFetch<{ ok: boolean; until: string }>(
        "/api/admin/results/unblock",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId, days }),
        },
      );
      const until = res?.until ? new Date(res.until).toLocaleString("pt-BR") : "";
      toast({ title: "Loja liberada", description: until ? `Bloqueio suspenso até ${until}.` : "Bloqueio suspenso." });
    } catch (err: any) {
      toast({
        title: "Erro",
        description: extractApiErrorMessage(err, "Não foi possível liberar a loja."),
        variant: "destructive",
      });
    } finally {
      setUnblock24hLoading(false);
    }
  };

  const handleUnblockUntil = async () => {
    if (unblock24hLoading || !storeId || !unblockUntilValue) return;
    setUnblock24hLoading(true);
    try {
      const res = await customFetch<{ ok: boolean; until: string }>(
        "/api/admin/results/unblock-until",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId, until: unblockUntilValue }),
        },
      );
      const until = res?.until
        ? new Date(res.until).toLocaleString("pt-BR", {
            timeZone: "America/Sao_Paulo",
            dateStyle: "short",
            timeStyle: "short",
          })
        : "";
      toast({ title: "Loja liberada", description: until ? `Liberada até ${until}.` : undefined });
      setUnblockUntilOpen(false);
    } catch (err: any) {
      toast({
        title: "Erro",
        description: extractApiErrorMessage(err, "Não foi possível liberar a loja."),
        variant: "destructive",
      });
    } finally {
      setUnblock24hLoading(false);
    }
  };

  // Pagamento manual (PIX direto, fora da Asaas). Marca o ciclo como pago e
  // deixa a régua normal correr até o vencimento novo — não é liberação
  // temporária nem cortesia.
  const handleManualPayment = async () => {
    if (manualPayLoading || !storeId) return;
    if (!(await confirm({
      title: `Registrar pagamento manual de "${storeName}"?`,
      description:
        "Use quando o lojista pagou por PIX direto, fora da Asaas. O plano fica pago por mais um ciclo e volta a ser cobrado normalmente no vencimento.",
      confirmLabel: "Registrar pagamento",
      destructive: false,
    }))) return;
    setManualPayLoading(true);
    try {
      const res = await customFetch<{ ok: boolean; nextDueDate: string; value: string }>(
        "/api/admin/results/manual-payment",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId }),
        },
      );
      const ate = res?.nextDueDate
        ? new Date(`${res.nextDueDate}T12:00:00-03:00`).toLocaleDateString("pt-BR")
        : "";
      toast({
        title: "Pagamento registrado",
        description: ate ? `Plano pago até ${ate}.` : "Plano liberado.",
      });
    } catch (err: any) {
      toast({
        title: "Erro",
        description: extractApiErrorMessage(err, "Não foi possível registrar o pagamento."),
        variant: "destructive",
      });
    } finally {
      setManualPayLoading(false);
    }
  };

  const handleCrmRetry = async () => {
    if (crmRetrying || !storeId) return;
    setCrmRetrying(true);
    try {
      await customFetch("/api/admin/results/crm-retry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId }),
      });
      toast({
        title: "Enviado pro OrganiZap",
        description: "Evento CrmRetry disparado com magic link.",
      });
    } catch (err: any) {
      toast({
        title: "Erro",
        description: err?.message || "Não foi possível enviar pro OrganiZap.",
        variant: "destructive",
      });
    } finally {
      setCrmRetrying(false);
    }
  };

  // O preview só é buscado com o diálogo aberto: ele bate na Asaas (pagamento
  // + saldo) e ninguém quer isso a cada render da tabela de busca.
  const { data: refundPreview, isFetching: refundLoading } = useQuery<RefundPreview>({
    queryKey: ["admin-results-refund-preview", storeId],
    queryFn: () => customFetch(`/api/admin/results/refund-preview/${storeId}`),
    enabled: refundOpen && !!storeId,
    staleTime: 0,
    gcTime: 0,
  });

  const handleRefund = async () => {
    if (refundSubmitting || !storeId) return;
    setRefundSubmitting(true);
    try {
      const r = await customFetch<{
        estornado: boolean;
        recorrenciaCancelada: boolean;
        planoRebaixado: boolean;
        comprovanteUrl: string | null;
        valor: number | null;
        avisos: string[];
      }>("/api/admin/results/refund", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId }),
      });
      const feito = [
        r.estornado ? `estornado ${r.valor != null ? brl(r.valor) : ""}`.trim() : null,
        r.recorrenciaCancelada ? "recorrência cancelada" : null,
        r.planoRebaixado ? "plano grátis" : null,
      ].filter(Boolean).join(" · ");
      toast({
        title: "Reembolso feito",
        description: [feito, ...r.avisos].filter(Boolean).join(" — "),
        action: r.comprovanteUrl ? (
          <a
            href={r.comprovanteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline whitespace-nowrap"
          >
            Comprovante
          </a>
        ) : undefined,
      });
      setRefundOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin-results-search"] });
      queryClient.invalidateQueries({ queryKey: ["admin-results-subscriptions"] });
      onDeleted();
    } catch (err: any) {
      toast({
        title: "Não reembolsou",
        description: extractApiErrorMessage(err) || err?.message || "Erro ao reembolsar.",
        variant: "destructive",
      });
    } finally {
      setRefundSubmitting(false);
    }
  };

  // 17 lojas em prod têm `name` string vazia (medido 01/08/2026). Com o nome
  // como palavra de confirmação isso virava `"" !== ""` → o botão vermelho
  // nascia habilitado com o campo em branco, justo nas linhas onde nem dá pra
  // saber que loja é. O slug é único e nunca vazio, então serve de identidade.
  const storeLabel = (storeName || "").trim() || (storeSlug || "").trim();
  const deleteStoreToken = storeLabel || "DELETAR";

  const handleDeleteStore = async () => {
    if (deleteStoreSubmitting || !storeId) return;
    setDeleteStoreSubmitting(true);
    try {
      await customFetch(`/api/admin/results/store/${storeId}`, { method: "DELETE" });
      toast({
        title: "Loja deletada",
        description: `${storeLabel ? `"${storeLabel}"` : "A loja"} e todos os dados relacionados foram removidos.`,
      });
      setDeleteStoreOpen(false);
      setDeleteStoreConfirmText("");
      onDeleted();
    } catch (err: any) {
      toast({
        title: "Erro ao deletar",
        description: err?.message || "Não foi possível deletar a loja.",
        variant: "destructive",
      });
    } finally {
      setDeleteStoreSubmitting(false);
    }
  };

  // Atalhos externos (impersonar / catálogo / agenda / api.whatsapp) só fazem
  // sentido quando há storeId/slug/whatsapp. Sem isso o item fica oculto.
  const digits = (whatsappNumber || whatsappFormatted || "").replace(/\D/g, "");
  const intl = digits ? (digits.startsWith("55") ? digits : `55${digits}`) : null;
  const greetingName = (firstName || "").trim().split(/\s+/)[0] || "";
  const whatsappHref = intl
    ? `https://api.whatsapp.com/send?phone=${intl}&text=${encodeURIComponent(`Olá! Falo com ${greetingName}?`)}`
    : null;
  const catalogHref = storeSlug ? `https://vendanozap.app/loja/${storeSlug}` : null;
  const agendaHref = storeSlug ? `https://vendanozap.app/agendar/${storeSlug}` : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 cursor-pointer">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {storeId && (
            <>
              <DropdownMenuItem onClick={() => onImpersonate(storeId)} className="cursor-pointer">
                <ExternalLinkIcon /> Impersonar Loja
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setVerAgente(true)} className="cursor-pointer" data-testid="results-ver-agente">
                <Bot className="w-4 h-4 mr-2" /> Ver Agente
              </DropdownMenuItem>
              {catalogHref && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <a href={catalogHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon /> Abrir Catálogo
                  </a>
                </DropdownMenuItem>
              )}
              {agendaHref && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <a href={agendaHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon /> Abrir Agenda
                  </a>
                </DropdownMenuItem>
              )}
              {whatsappHref && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon /> Conversar no WhatsApp
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCopySubscribeLink} disabled={subLinkLoading} className="cursor-pointer">
                {subLinkCopied ? (
                  <Check className="w-4 h-4 mr-2 text-green-600" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                Link para Assinar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleUnblock(1)} disabled={unblock24hLoading} className="cursor-pointer">
                <Unlock className="w-4 h-4 mr-2" />
                Liberar por 24 horas
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleUnblock(10)} disabled={unblock24hLoading} className="cursor-pointer">
                <Unlock className="w-4 h-4 mr-2" />
                Liberar por 10 dias
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setUnblockUntilValue(`${agoraEmBrasilia().slice(0, 10)}T23:59`);
                  setUnblockUntilOpen(true);
                }}
                disabled={unblock24hLoading}
                className="cursor-pointer"
              >
                <Unlock className="w-4 h-4 mr-2" />
                Liberar até…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleManualPayment} disabled={manualPayLoading} className="cursor-pointer">
                <HandCoins className="w-4 h-4 mr-2" />
                Registrar pagamento manual
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCrmRetry} disabled={crmRetrying} className="cursor-pointer">
                <Send className="w-4 h-4 mr-2" />
                Enviar para OrganiZap
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {storeId && (
            <DropdownMenuItem
              onClick={() => setRefundOpen(true)}
              className="cursor-pointer text-red-600 focus:text-red-600"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              Reembolsar
            </DropdownMenuItem>
          )}
          {subscriptionId && (
            <DropdownMenuItem
              onClick={() => setConfirmOpen(true)}
              className="cursor-pointer text-red-600 focus:text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Deletar Assinatura
            </DropdownMenuItem>
          )}
          {storeId && (
            <DropdownMenuItem
              onClick={() => setDeleteStoreOpen(true)}
              className="cursor-pointer text-red-600 focus:text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Deletar Loja
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {verAgente && storeId && <VerAgenteModal storeId={storeId} storeName={storeName} onClose={() => setVerAgente(false)} />}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deletar assinatura</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja deletar permanentemente a assinatura de{" "}
              <span className="font-medium">{clientName}</span>? Esta ação não pode ser desfeita
              e o registro será removido do banco de dados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deletando…" : "Deletar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unblockUntilOpen} onOpenChange={setUnblockUntilOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Liberar até</DialogTitle>
            <DialogDescription>{storeLabel || "Loja"} · horário de Brasília</DialogDescription>
          </DialogHeader>
          <DataBr
            tipo="datetime-local"
            value={unblockUntilValue}
            min={agoraEmBrasilia()}
            onChange={setUnblockUntilValue}
            className="h-9 w-full px-3 rounded-md border text-sm bg-background"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setUnblockUntilOpen(false)} disabled={unblock24hLoading}>
              Cancelar
            </Button>
            <Button onClick={handleUnblockUntil} disabled={unblock24hLoading || !unblockUntilValue}>
              {unblock24hLoading ? "Liberando…" : "Liberar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={refundOpen} onOpenChange={(o) => setRefundOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reembolsar</DialogTitle>
            <DialogDescription className="pt-2">
              Devolve o último recebimento na Asaas, cancela a recorrência e volta a
              loja pro plano grátis. O estorno de PIX cai na conta do lojista em segundos.
            </DialogDescription>
          </DialogHeader>

          {refundLoading || !refundPreview ? (
            <p className="py-3 text-sm text-muted-foreground">Consultando a Asaas…</p>
          ) : (
            <div className="space-y-3 py-1">
              <dl className="rounded-md border divide-y text-sm">
                <div className="flex justify-between gap-3 px-3 py-2">
                  <dt className="text-muted-foreground">Loja</dt>
                  <dd className="font-medium text-right truncate">
                    {refundPreview.store.name || refundPreview.store.slug || "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 px-3 py-2">
                  <dt className="text-muted-foreground">Pagamento</dt>
                  <dd className="font-medium text-right">
                    {refundPreview.pagamento ? (
                      <>
                        {brl(refundPreview.pagamento.value)}{" "}
                        <span className="text-muted-foreground font-normal">
                          · {refundPreview.pagamento.billingType}
                          {refundPreview.pagamento.paymentDate
                            ? ` · ${refundPreview.pagamento.paymentDate.split("-").reverse().join("/")}`
                            : ""}
                        </span>
                      </>
                    ) : "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 px-3 py-2">
                  <dt className="text-muted-foreground">Recorrência</dt>
                  <dd className="font-medium text-right">
                    {refundPreview.assinatura
                      ? `${refundPreview.assinatura.cycle} · ${refundPreview.assinatura.status}${
                          refundPreview.assinatura.authorizationId ? " · mandato PIX vai ser cancelado" : ""
                        }`
                      : "nenhuma"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 px-3 py-2">
                  <dt className="text-muted-foreground">Plano</dt>
                  <dd className="font-medium text-right">
                    {refundPreview.store.plan || "—"} → grátis
                  </dd>
                </div>
              </dl>

              {!refundPreview.ok && (
                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
                  {refundPreview.motivo || "Não dá pra reembolsar agora."}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRefundOpen(false)} disabled={refundSubmitting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleRefund}
              disabled={refundSubmitting || refundLoading || !refundPreview?.ok}
            >
              {refundSubmitting
                ? "Reembolsando…"
                : refundPreview?.ok && refundPreview.pagamento
                  ? `Reembolsar ${brl(refundPreview.pagamento.value)}`
                  : "Reembolsar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteStoreOpen}
        onOpenChange={(o) => {
          setDeleteStoreOpen(o);
          if (!o) setDeleteStoreConfirmText("");
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Deletar loja permanentemente</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <div>
                Esta ação <span className="font-semibold">não pode ser desfeita</span>.
                Vai apagar <span className="font-semibold">{storeLabel ? `"${storeLabel}"` : "esta loja"}</span> e
                em cascata: usuário, produtos, pedidos, agendamentos, clientes,
                financeiro, push subscriptions, magic links, cartões e assinaturas
                no banco.
              </div>
              <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                ⚠ <span className="font-semibold">Assinaturas Asaas ativas continuam
                cobrando o cliente</span> — cancele lá antes de deletar se for o caso.
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-sm">
              Pra confirmar, digite <span className="font-mono font-semibold">{deleteStoreToken}</span> abaixo:
            </Label>
            <input
              type="text"
              value={deleteStoreConfirmText}
              onChange={(e) => setDeleteStoreConfirmText(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
              placeholder={deleteStoreToken}
              autoComplete="off"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteStoreOpen(false)} disabled={deleteStoreSubmitting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteStore}
              disabled={deleteStoreSubmitting || deleteStoreConfirmText.trim() !== deleteStoreToken}
            >
              {deleteStoreSubmitting ? "Deletando…" : "Deletar permanentemente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}

function ExternalLinkIcon() {
  return <ExternalLink className="w-4 h-4 mr-2" />;
}

function SlugMessagesPanel() {
  const { data, refetch, isLoading } = useQuery<{
    produtos: string | null;
    servicos: string | null;
    ambos: string | null;
    defaults: { produtos: string; servicos: string; ambos: string };
  }>({
    queryKey: ["/api/admin/uazapi/slug-messages"],
    queryFn: () => customFetch("/api/admin/uazapi/slug-messages", { method: "GET" }),
    staleTime: 0,
  });
  const { toast } = useToast();
  const [draft, setDraft] = useState<{ produtos: string; servicos: string; ambos: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data || draft) return;
    setDraft({
      produtos: data.produtos ?? "",
      servicos: data.servicos ?? "",
      ambos: data.ambos ?? "",
    });
  }, [data, draft]);

  const changed = !!data && !!draft && (
    (draft.produtos || null) !== (data.produtos || null) ||
    (draft.servicos || null) !== (data.servicos || null) ||
    (draft.ambos || null) !== (data.ambos || null)
  );

  async function handleSave() {
    if (!draft || saving) return;
    setSaving(true);
    try {
      await customFetch("/api/admin/uazapi/slug-messages", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          produtos: draft.produtos.trim() || null,
          servicos: draft.servicos.trim() || null,
          ambos: draft.ambos.trim() || null,
        }),
      });
      toast({ title: "Mensagens salvas", description: "Aviso de troca de link atualizado." });
      setDraft(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: extractApiErrorMessage(err, "Falha ao salvar."), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const fields: { key: "produtos" | "servicos" | "ambos"; label: string; hint: string }[] = [
    { key: "produtos", label: "Loja de produtos (envia o link /loja)", hint: "Placeholders: {nome}, {loja_link}" },
    { key: "servicos", label: "Loja de serviços (envia o link /agendar)", hint: "Placeholders: {nome}, {agendar_link}" },
    { key: "ambos", label: "Produtos + serviços (envia os dois links)", hint: "Placeholders: {nome}, {loja_link}, {agendar_link}" },
  ];

  return (
    <Card className="p-6 space-y-4">
      <div>
        <div className="flex items-center gap-2 text-lg font-semibold"><Send className="w-5 h-5 text-primary" /> Mensagem de troca de link (slug)</div>
        <p className="text-sm text-muted-foreground mt-1">
          Enviada por WhatsApp ao lojista (número da loja + número de quem editou) quando o link da loja muda. Mesmo fallback UAZAPI/Z-API. Vazio = usa o texto padrão.
        </p>
      </div>
      {isLoading || !draft ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <>
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm font-medium">{f.label}</label>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => setDraft((d) => (d ? { ...d, [f.key]: data!.defaults[f.key] } : d))}
                >
                  Usar padrão
                </button>
              </div>
              <textarea
                value={draft[f.key]}
                onChange={(e) => setDraft((d) => (d ? { ...d, [f.key]: e.target.value } : d))}
                rows={4}
                placeholder={data!.defaults[f.key]}
                className="w-full rounded-md border border-input bg-background p-2.5 text-sm"
              />
              <p className="text-[11px] text-muted-foreground">{f.hint}</p>
            </div>
          ))}
          <Button onClick={handleSave} disabled={!changed || saving}>
            {saving ? "Salvando…" : "Salvar mensagens"}
          </Button>
        </>
      )}
    </Card>
  );
}

